import React from 'react';
import { useTranslation } from 'react-i18next';

import { QrCodeFieldConfig, QrFieldSource } from 'editor/src/store/design/types';

import { QrFieldDef } from 'editor/src/lib/qr';

import styles from './index.module.scss';

const STATIC_OPTION = 'static';
const ELEMENT_OPTION_PREFIX = 'el:';

interface Props {
  def: QrFieldDef;
  field: QrCodeFieldConfig | undefined;
  textFields: { uuid: number; name: string }[];
  onChange: (source: QrFieldSource | undefined) => void;
}

// A single field-mapping row in the QR configuration dialog: label + source picker
// (not included / a personalizable text element / static text) and, for static
// sources, the value input — a textarea, an options <select>, or a plain text input
// depending on the field definition.
function FieldSourceRow({ def, field, textFields, onChange }: Props) {
  const { t } = useTranslation();
  const source = field?.source;

  // Fields with a fixed set of options (WiFi encryption/hidden) are always static —
  // there is nothing to link to a personalizable element, so the source select is
  // skipped entirely and the options select is bound straight to the static value.
  if (def.options) {
    const value = source?.type === 'static' ? source.value : def.options[0]?.value ?? '';
    return (
      <div className={styles.fieldRow}>
        <span className={styles.fieldLabel}>{t(def.label)}</span>
        <select
          className={`${styles.fieldSelect} cy-qr-field-${def.key}`}
          value={value}
          onChange={(e) => onChange({ type: 'static', value: e.target.value })}
        >
          {def.options.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  let selectValue = '';
  if (source?.type === 'static') {
    selectValue = STATIC_OPTION;
  } else if (source?.type === 'element') {
    selectValue = `${ELEMENT_OPTION_PREFIX}${source.elementId}`;
  }

  function onSourceSelect(value: string) {
    if (value === STATIC_OPTION) {
      onChange({ type: 'static', value: source?.type === 'static' ? source.value : '' });
    } else if (value.startsWith(ELEMENT_OPTION_PREFIX)) {
      onChange({ type: 'element', elementId: Number(value.slice(ELEMENT_OPTION_PREFIX.length)) });
    } else {
      onChange(undefined);
    }
  }

  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{t(def.label)}</span>
      <select
        className={`${styles.fieldSelect} cy-qr-field-${def.key}`}
        value={selectValue}
        onChange={(e) => onSourceSelect(e.target.value)}
      >
        <option value="">{t('Not included')}</option>
        {textFields.map((textField) => (
          <option key={textField.uuid} value={`${ELEMENT_OPTION_PREFIX}${textField.uuid}`}>
            {textField.name}
          </option>
        ))}
        <option value={STATIC_OPTION}>{t('Static text')}</option>
      </select>
      {source?.type === 'static' &&
        (def.multiline ? (
          <textarea
            className={`${styles.fieldStaticTextarea} cy-qr-static-${def.key}`}
            value={source.value}
            placeholder={t(def.label)}
            onChange={(e) => onChange({ type: 'static', value: e.target.value })}
          />
        ) : (
          <input
            className={`${styles.fieldStaticInput} cy-qr-static-${def.key}`}
            type="text"
            value={source.value}
            placeholder={t(def.label)}
            onChange={(e) => onChange({ type: 'static', value: e.target.value })}
          />
        ))}
    </div>
  );
}

export default React.memo(FieldSourceRow);
